import { graphql } from 'react-relay';
import React, { useState, useContext, useCallback, Fragment } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import AkModalDialog, {
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalBody,
} from '@atlaskit/modal-dialog';
import SectionMessage from '@atlaskit/section-message';
import Button from '@atlaskit/button/new';
import ButtonGroup from '@atlaskit/button/button-group';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Flex, Stack, Box, xcss } from '@atlaskit/primitives';

import { CONVERT_PAGE_TO_FOLDER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { stopExperienceOnError } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { ScreenEvent } from '@confluence/analytics/entry-points/ScreenEvent.ts';
import { FOLDER_VIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { generateSlug } from '@confluence/route-manager-utils/entry-points/url.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	useApprovals,
	ApprovalsProvider,
} from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import type {
	ConvertPageToFolderDialogConvertPageToFolderMutation as ConvertPageToFolderDialogConvertPageToFolderMutation$data,
	ConvertPageToFolderDialogConvertPageToFolderMutationVariables as ConvertPageToFolderDialogConvertPageToFolderMutation$variables,
} from './__types__/ConvertPageToFolderDialogConvertPageToFolderMutation.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const COMPONENT_NAME = 'ConvertPageToFolderDialogModal';
const source = 'VIEW_PAGE';

const warningMessageStyles = xcss({
	marginBottom: 'space.100',
});

// When a page is archived for folder conversion, it is renamed to match the format `Archived ${contentTitle} abCd`
// with the last 4 characters being random. If this exceeds the max title length, the backend will throw an error
export const MAX_FOLDER_CONVERSION_TITLE_LENGTH = 241;
type ConvertPageToFolderDialogProps = {
	spaceKey: string;
	contentId: string;
	contentTitle?: string | null;
	onClose: () => void;
};
const ConvertPageToFolderDialogComponent = ({
	spaceKey,
	contentId,
	contentTitle,
	onClose,
	flags,
}: ConvertPageToFolderDialogProps & WithFlagsProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { push } = useRouteActions();
	const { edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;
	const { approvalRequest } = useApprovals();
	const hasApprovalReview = Boolean(approvalRequest?.review);
	const [isConverting, setIsConverting] = useState(false);
	const [convertPageToFolderMutation, { error: convertPageToFolderError }] = useMutationInterop<
		ConvertPageToFolderDialogConvertPageToFolderMutation$data,
		ConvertPageToFolderDialogConvertPageToFolderMutation$variables
	>(graphql`
		mutation ConvertPageToFolderDialogConvertPageToFolderMutation($id: ID!) {
			convertToFolder(id: $id) {
				content {
					id
				}
			}
		}
	`);
	const handleOnClose = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'clicked',
				actionSubject: COMPONENT_NAME,
				actionSubjectId: `${COMPONENT_NAME}CancelButton`,
				source,
			},
		}).fire();
		onClose();
	}, [createAnalyticsEvent, onClose]);
	const convertPageToFolderSuccess = (data: {
		data?: ConvertPageToFolderDialogConvertPageToFolderMutation$data;
	}) => {
		setIsConverting(false);
		experienceTracker.succeed({
			name: CONVERT_PAGE_TO_FOLDER_EXPERIENCE,
		});
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: COMPONENT_NAME,
				action: 'success',
				attributes: undefined,
				source,
			},
		}).fire();
		void flags.showFlag({
			type: 'success-circle',
			title: formatMessage(i18n.successFlagTitle),
			description: formatMessage(i18n.successFlagDescription),
			close: 'auto',
		});
		const contentId = data?.data?.convertToFolder?.content?.id;
		if (contentId) {
			const newlyCreatedFolderUrl = FOLDER_VIEW.toUrl({
				spaceKey,
				contentId,
				...(expVal('confluence_folder_view', 'isEnabled', false) && {
					contentSlug: generateSlug(contentTitle),
				}),
			});
			newlyCreatedFolderUrl && push(newlyCreatedFolderUrl);
		}
		onClose();
	};
	const showErrorFlag = ({
		title,
		description,
		actions,
	}: {
		title: string;
		description: string;
		actions?: {
			content: string;
			onClick: () => void;
		}[];
	}) => {
		void flags.showFlag({
			type: 'error',
			title,
			description,
			close: 'auto',
			actions,
		});
	};
	const abortExperienceOnExpectedError = (error: ApolloErrorInterop) => {
		markErrorAsHandled(error);
		experienceTracker.abort({
			name: CONVERT_PAGE_TO_FOLDER_EXPERIENCE,
			reason: 'Expected Apollo error',
		});
	};
	const convertPageToFolderFailure = (error: ApolloErrorInterop) => {
		if (isPageEditRestrictedError(error)) {
			showErrorFlag({
				title: formatMessage(i18n.editPermissionErrorFlagTitle),
				description: formatMessage(i18n.editPermissionErrorFlagDescription),
			});
			abortExperienceOnExpectedError(error);
		} else if (isNameCollisionError(error)) {
			showErrorFlag({
				title: formatMessage(i18n.nameCollisionErrorFlagTitle),
				description: formatMessage(i18n.nameCollisionErrorFlagDescription),
			});
			abortExperienceOnExpectedError(error);
		} else if (isNameLengthLimitError(error) && contentTitle) {
			showErrorFlag({
				title: formatMessage(i18n.nameLengthLimitErrorFlagTitle),
				description: formatMessage(i18n.nameLengthLimitErrorFlagDescription, {
					numCharsOverLimit: contentTitle.length - MAX_FOLDER_CONVERSION_TITLE_LENGTH,
				}),
			});
			abortExperienceOnExpectedError(error);
		} else {
			showErrorFlag({
				title: formatMessage(i18n.defaultErrorFlagTitle),
				description: formatMessage(i18n.defaultErrorFlagDescription),
				actions: [
					{
						content: formatMessage(i18n.defaultErrorFlagAction),
						onClick: handlePageToFolderConversion,
					},
				],
			});
			if (isExpectedApolloError(error)) {
				abortExperienceOnExpectedError(error);
				return;
			}
			stopExperienceOnError(CONVERT_PAGE_TO_FOLDER_EXPERIENCE, error);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					actionSubject: COMPONENT_NAME,
					action: 'failed',
					attributes: { error },
					actionSubjectId: `${COMPONENT_NAME}ConvertButton`,
					source,
				},
			}).fire();
		}
	};
	const handlePageToFolderConversion = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: COMPONENT_NAME,
				actionSubjectId: `${COMPONENT_NAME}ConvertButton`,
				source,
			},
		}).fire();
		setIsConverting(true);
		experienceTracker.start({
			name: CONVERT_PAGE_TO_FOLDER_EXPERIENCE,
		});
		convertPageToFolderMutation({
			variables: {
				id: contentId,
			},
		})
			.then((data) => {
				convertPageToFolderSuccess(data);
			})
			.catch((error) => {
				convertPageToFolderFailure(error);
				setIsConverting(false);
			});
	};

	return (
		<Fragment>
			<AkModalDialog
				width="450px"
				key="ConvertPageToFolderDialog"
				onClose={handleOnClose}
				shouldScrollInViewport
			>
				<ModalBody>
					<ModalHeader hasCloseButton>
						<ModalTitle>
							<FormattedMessage {...i18n.dialogHeader} />
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Stack space="space.100">
							{isPremium &&
								hasApprovalReview &&
								expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
									<Box xcss={warningMessageStyles}>
										<SectionMessage appearance="warning">
											<FormattedMessage {...i18n.approvalsWarning} />
										</SectionMessage>
									</Box>
								)}
							<FormattedMessage {...i18n.dialogDescription} />
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Stack space="space.200">
							<Flex justifyContent="end">
								<ButtonGroup>
									<Button onClick={handleOnClose} appearance="subtle">
										<FormattedMessage {...i18n.cancel} />
									</Button>

									<Button
										onClick={handlePageToFolderConversion}
										appearance="primary"
										isLoading={isConverting}
									>
										<FormattedMessage {...i18n.dialogSubmitText} />
									</Button>
								</ButtonGroup>
							</Flex>
						</Stack>
					</ModalFooter>
				</ModalBody>
			</AkModalDialog>
			<ScreenEvent
				name="convertPageToFolderDialogModalScreen"
				id="convertPageToFolderModalScreenEvent"
			/>
			{convertPageToFolderError && <ErrorDisplay error={convertPageToFolderError} />}
		</Fragment>
	);
};
const ConvertPageToFolderDialogWithFlags = withFlags(ConvertPageToFolderDialogComponent);

export const ConvertPageToFolderDialog = (props: ConvertPageToFolderDialogProps) => (
	<ApprovalsProvider contentId={props.contentId}>
		<ConvertPageToFolderDialogWithFlags {...props} />
	</ApprovalsProvider>
);
const isPageEditRestrictedError = (error: ApolloErrorInterop): boolean =>
	error.message.includes('User does not have permissions to perform this action');
const isNameCollisionError = (error: ApolloErrorInterop): boolean =>
	error.message.includes('A folder exists with the same title in this space');
const isNameLengthLimitError = (error: ApolloErrorInterop): boolean =>
	error.message.includes('Title cannot be longer than 255 characters');
const i18n = defineMessages({
	dialogHeader: {
		id: 'folder-conversion.convert-page-to-folder.dialog.header',
		description: 'Heading for convert page to folder dialog',
		defaultMessage: 'Convert this page into a folder?',
	},
	dialogDescription: {
		id: 'folder-conversion.convert-page-to-folder.dialog.description',
		description: 'Description for convert page to folder dialog',
		defaultMessage:
			'We’ll archive the original page, so you don’t need to worry about losing your work.',
	},
	cancel: {
		id: 'folder-conversion.convert-page-to-folder.dialog.cancel',
		defaultMessage: 'Cancel',
		description: 'Cancel button text for convert page to folder dialog',
	},
	dialogSubmitText: {
		id: 'folder-conversion.convert-page-to-folder.dialog.submit',
		defaultMessage: 'Convert',
		description: 'Text for completing conversion of page to folder',
	},
	approvalsWarning: {
		id: 'folder-conversion.convert-page-to-folder.dialog.approvalsWarning',
		defaultMessage: 'This will end an approval.',
		description: 'Warning message shown when converting a page into a folder',
	},
	successFlagTitle: {
		id: 'folder-conversion.convert-page-to-folder.flags.success-message.title',
		defaultMessage: 'Conversion complete',
		description: 'Flag title shown after conversion of page to folder succeeds',
	},
	successFlagDescription: {
		id: 'folder-conversion.convert-page-to-folder.flags.success-message.description.placeholder-title',
		defaultMessage: 'We’ve finished converting the page to a folder.',
		description: 'Flag description shown after conversion of page to folder succeeds',
	},
	defaultErrorFlagTitle: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.title',
		defaultMessage: 'We couldn’t convert the page to a folder',
		description: 'Flag title shown after conversion of page to folder failed',
	},
	defaultErrorFlagDescription: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.description.placeholder-title',
		defaultMessage: 'Something went wrong, and we were unable to convert this page to a folder.',
		description: 'Flag description shown after conversion of page to folder failed',
	},
	defaultErrorFlagAction: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.action',
		defaultMessage: 'Try again',
		description:
			'Flag action button shown after conversion of page to folder failed. Clicking on this will retry the folder conversion operation',
	},
	editPermissionErrorFlagTitle: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.edit-permissions.title',
		defaultMessage: 'Request edit and archive access to continue',
		description:
			'Flag title shown after conversion of page to folder failed due to user not having edit and/or archive permissions for the page',
	},
	editPermissionErrorFlagDescription: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.edit-permissions.description',
		defaultMessage:
			'To convert this page to a folder, you’ll need permission to edit and archive it.',
		description:
			'Flag description shown after conversion of page to folder failed due to user not having edit permissions for the page',
	},
	nameCollisionErrorFlagTitle: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.name-collision.title',
		defaultMessage: 'Rename this page to continue',
		description:
			'Flag title shown after conversion of page to folder failed due to the page, which is renamed so that a folder with the same name can be created, having the same title as another page',
	},
	nameCollisionErrorFlagDescription: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.name-collision.description',
		defaultMessage:
			'To avoid duplicate titles, you’ll need to rename this page before we can convert it to a folder and archive the original.',
		description:
			'Flag description shown after conversion of page to folder failed due to the page, which is renamed so that a folder with the same name can be created, having the same title as another page',
	},
	nameLengthLimitErrorFlagTitle: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.name-length-limit.title',
		defaultMessage: 'Give this page a shorter title',
		description:
			'Flag title shown after conversion of page to folder failed due to the page, which is renamed so that a folder with the same name can be created, exceeding the max title character limit',
	},
	nameLengthLimitErrorFlagDescription: {
		id: 'folder-conversion.convert-page-to-folder.flags.error-message.name-length-limit.description',
		defaultMessage:
			'Before we can convert this page to a folder, archiving the original, you’ll need to give it a title that is at least {numCharsOverLimit, plural, one {# character} other {# characters}} shorter.',
		description:
			'Flag description shown after conversion of page to folder failed due to the page, which is renamed so that a folder with the same name can be created, exceeding the max title character limit',
	},
});
